import React, { useEffect, useState, useCallback } from "react";
import config from "../../constants/config";
import { fetchReq } from "../../api/fetch";
import api from "../../api";

const { ENV_SUFIX } = config;

const Proxy = () => {
  const [showLoginMessage, setShowLoginMessage] = useState(false);
  window?.tfIdentityGlobals?.showLoader();

  const getCookie = useCallback((name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
  }, []);

  const deleteCookie = useCallback((name, domain = ".thermofisher.com") => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; domain=${process.env[`REACT_APP_COOKIE_DOMAIN${ENV_SUFIX}`]};`;
  }, []);

  const loadScript = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);

      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  // const loadDynamicCSS = useCallback(async () => {
  //   const clientId = getCookie("rp_clientid");

  //   try {
  //     await loadScript("/identity/static/js/tfidentity/fsClientIds.js");

  //     const allClients = Object.values(window.fsClientIds || {}).flat();
  //     const matchedClient = allClients.find(
  //       (entry) => entry.clientId === clientId
  //     );

  //     const cssLink = document.createElement("link");
  //     cssLink.rel = "stylesheet";

  //     if (matchedClient) {
  //       const hostname = matchedClient.name;

  //       cssLink.href = `https://${hostname}/designsystem-cdn/design-tokens/fsc_variables.css`;

  //       const faviconLink = document.createElement("link");
  //       faviconLink.rel = "icon";
  //       faviconLink.href = `https://${hostname}/favicon.ico`;
  //       document.head.appendChild(faviconLink);
  //     } else {
  //       cssLink.href = "/designsystem-cdn/design-tokens/core_variables.css";
  //     }

  //     document.head.appendChild(cssLink);
  //   } catch (error) {
  //     console.error("Failed to load dynamic CSS:", error);
  //   }
  // }, [getCookie, loadScript]);

  const loadGigyaScript = useCallback(() => {
    return loadScript(
      `https://${process.env[`REACT_APP_CDC_HOST_NAME${ENV_SUFIX}`]}/js/gigya.js?apiKey=${process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`]}`
    );
  }, [loadScript]);

  const getRedirectHostname = useCallback((url) => {
    try {
      const searchParams = new URL(url).searchParams;

      const redirectUrl =
        searchParams.get("redirect_uri") ||
        searchParams.get("signInRedirectUri") ||
        searchParams.get("regRedirectUri");

      return redirectUrl
        ? new URL(decodeURIComponent(redirectUrl)).hostname
        : "";
    } catch (error) {
      console.warn("Failed to resolve redirect hostname:", error.message);
      return "";
    }
  }, []);

  const determineLoginLink = useCallback(async () => {
    const referrerPage = document.referrer || "";
    const cipRu = getCookie("cip_ru");
    const isoCode = getCookie("CK_ISO_CODE");

    sessionStorage.setItem("referrer_state", referrerPage);
    sessionStorage.removeItem("signin_username");

    let loginLink = "signin-identifier.html";

    if (isoCode?.toLowerCase() === "cn") {
      sessionStorage.removeItem("mobileNumber");
      sessionStorage.removeItem("pflCompletion");

      let response = [];

      try {
        const rawResponse = await fetchReq(api.config.findRpUrls());
        response = await rawResponse.json();
        console.log("✅ Parsed RP URLs:", response);
      } catch (error) {
        console.error("Failed to fetch RP URLs:", error);
        response = [];
      }

      const rpResponse = Array.isArray(response) ? response : [];

      sessionStorage.setItem("rp_url", JSON.stringify(rpResponse));

      if (cipRu === "email") {
        loginLink = "simplified-username.html";
      } else {
        const hasCnDomain = rpResponse.some((obj) => {
          const redirectHost = getRedirectHostname(obj?.url);

          return (
            redirectHost === "thermofisher.cn" ||
            (
              redirectHost.endsWith(".thermofisher.cn") &&
              !redirectHost.endsWith("fisherbioreagents.thermofisher.cn")
            )
          );
        });

        loginLink = hasCnDomain
          ? "signin-identifier.html"
          : "/identity/mobile/signin";
      }
    } else if (cipRu === "true") {
      loginLink = "simplified-username.html";
    }

    if (
      referrerPage.includes("/signin-identifier?regType=FULL") ||
      referrerPage.includes("lt?cmd=ViewCart&ShoppingCartKey")
    ) {
      loginLink = "signin-identifier?regType=FULL";
    }

    return loginLink;
  }, [getCookie, getRedirectHostname]);

  const cleanupURL = useCallback(() => {
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.href.split("#")[0]);
    }
  }, []);

  const checkLoginPopup = useCallback(() => {
    if (localStorage.getItem("loginPopup") === "open") {
      setShowLoginMessage(true);

      if (document.referrer.indexOf("proxy.html") > 0) {
        localStorage.removeItem("loginPopup");
      }
    }
  }, []);

  const loadOIDCScript = useCallback((loginLink) => {
    const scriptElement = document.createElement("script");
    scriptElement.type = "text/javascript";
    scriptElement.src =
      `https://${process.env[`REACT_APP_CDC_HOST_NAME${ENV_SUFIX}`]}/js/gigya.oidc.js?apiKey=${process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`]}`;
    scriptElement.async = true;
    scriptElement.innerText =
      `{ loginURL: "${loginLink}", consentURL: "/api-gateway/identity-authorization/identity/auth/consent", errorURL: "loginError.html" }`;

    document.head.appendChild(scriptElement);
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = "#827f7f";

    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    const initializeProxy = async () => {
      try {
        // await loadDynamicCSS();
        await loadGigyaScript();

        deleteCookie("cip_identifier");

        const loginLink = await determineLoginLink();

        cleanupURL();

        const gacCookie = getCookie(
          `gac_${process.env[`REACT_APP_CDC_API_KEY${ENV_SUFIX}`]}`
        );

        if (gacCookie && window.gigya?.accounts?.addEventHandlers) {
          window.gigya.accounts.addEventHandlers({
            onLogin: () => loadOIDCScript(loginLink),
          });
        } else {
          loadOIDCScript(loginLink);
        }

        checkLoginPopup();
      } catch (error) {
        console.error("Proxy initialization failed:", error);
        window.tfIdentityGlobals?.hideLoader();
        window.location.href =
          "/account-center/loginError.html?source=gigyajs-proxy";
      }
    };

    initializeProxy();
  }, [
    loadGigyaScript,
    deleteCookie,
    determineLoginLink,
    cleanupURL,
    getCookie,
    loadOIDCScript,
    checkLoginPopup,
  ]);

  return (
    <div className="proxy-page">

      {showLoginMessage && (
        <div
          id="loginMsg"
          className="login-message"
          style={{ textAlign: "center", marginTop: "20px" }}
        >
          <span>Verifying your credentials..</span>
          <br />
          <span>Please do not close this window.</span>
        </div>
      )}
    </div>
  );
};

export default Proxy;